/* eslint-disable arrow-body-style */
import React, { useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import Paper from '@material-ui/core/Paper';
import ReactMapboxGl, { Marker, Layer, Feature } from 'react-mapbox-gl';
import { CallContext } from 'context/callContextContainer';
import { CircularProgressContext } from 'components/CircularProgressOverlay';
import { PergoTitle } from '../PergoTitle';
import AsyncMap from './AsyncMap';
import { MapContext } from '../../context/mapContext/mapContextContainer';
import { JobContext } from '../../context/jobContextContainer';
import { fetchMapboxDrivingRoute } from '../../utils/mapboxDirections';
import { computeRouteViewMapLayers } from '../../utils/routeViewMapLayers';
import { buildRouteStopMarkers } from '../../utils/mapRouteMarkerLayout';
import GreenIcon from '../../images/marker_green.png';
import TmpMapBack from '../../images/tmp-map.png';

const moment = require('moment-timezone');

const Map =
  typeof window !== 'undefined' &&
  ReactMapboxGl({
    accessToken: process.env.MAPBOX_ACCESS_TOKEN,
  });

const PickupMarkerDot = () => <span className="map-marker-dot map-marker-dot--pickup" aria-hidden />;
const DropoffMarkerDot = () => <span className="map-marker-dot map-marker-dot--dropoff" aria-hidden />;

const getTripRouteColor = detail => (detail.status == 8 ? '#25d84f' : '#178cc7');

const getRouteHoverFocus = (hoveredJobId, mapRouteDirections) => {
  if (hoveredJobId == null || !mapRouteDirections?.length) {
    return { jobId: null, prevJobId: null, connectorId: null };
  }

  const jobId = String(hoveredJobId);
  const index = mapRouteDirections.findIndex(detail => String(detail.id) === jobId);
  if (index < 0) {
    return { jobId, prevJobId: null, connectorId: null };
  }

  const prevDetail = index > 0 ? mapRouteDirections[index - 1] : null;
  return {
    jobId,
    prevJobId: prevDetail ? String(prevDetail.id) : null,
    connectorId: prevDetail ? `connector-${prevDetail.id}-${jobId}` : null,
  };
};

const getTripRoutePaint = (detail, hoverFocus) => {
  const color = getTripRouteColor(detail);
  const jobId = String(detail.id);
  const hasHoverFocus = hoverFocus?.jobId != null;

  if (!hasHoverFocus) {
    return { 'line-color': color, 'line-width': 5, 'line-opacity': 1 };
  }
  if (hoverFocus.jobId === jobId) {
    return { 'line-color': color, 'line-width': 9, 'line-opacity': 1 };
  }
  return { 'line-color': color, 'line-width': 4, 'line-opacity': 0.22 };
};

const getConnectorPaint = (connector, hoverFocus) => {
  const base = {
    'line-color': '#ff9d2e',
    'line-dasharray': [2, 2],
  };

  if (!hoverFocus?.jobId) {
    return { ...base, 'line-width': 4, 'line-opacity': 1 };
  }
  if (connector.id === hoverFocus.connectorId) {
    return { ...base, 'line-width': 7, 'line-opacity': 1 };
  }
  return { ...base, 'line-width': 3, 'line-opacity': 0.25 };
};

const isRouteStopFocused = (stop, hoverFocus) => {
  if (!hoverFocus?.jobId) {
    return false;
  }
  if (String(stop.jobId) === hoverFocus.jobId) {
    return true;
  }
  return (
    hoverFocus.prevJobId != null &&
    String(stop.jobId) === hoverFocus.prevJobId &&
    stop.stopType === 'dropoff'
  );
};

const MapSection = ({ mapType }) => {
  const [directions, setDirections] = useState(null);
  const [mapRouteDirections, setMapRouteDirections] = useState(null);
  const [mapRouteConnectors, setMapRouteConnectors] = useState(null);
  const [hoveredRouteJobId, setHoveredRouteJobId] = useState(null);
  const mapRef = useRef(null);
  const markerHoverRef = useRef(false);
  const { mapData, setMapData } = useContext(MapContext);
  const { isRouteViewMode } = useContext(JobContext);
  const [fitBounds, setFitBounds] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const { siteSetting } = useContext(CallContext);
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);

  async function getDirections() {
    const Location1Str = `${mapData.pickup_lng},${mapData.pickup_lat}`;
    const Location2Str = `${mapData.dropoff_lng},${mapData.dropoff_lat}`;
    const apiUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${Location1Str};${Location2Str}?alternatives=true&geometries=geojson&language=en&overview=full&steps=true&access_token=${process.env.MAPBOX_ACCESS_TOKEN}`;
    const fetchResult = await fetch(apiUrl);
    const Result = await fetchResult.json();

    let lat;
    let lon;
    let deadhead_distance = 0;
    if (!siteSetting() || siteSetting().lat == '0' || siteSetting().lon == '0') {
      lat = 0;
      lon = 0;
    } else {
      lat = siteSetting().lat / 1000000.0;
      lon = siteSetting().lon / 1000000.0;
    }

    if (lat != 0 && lon != 0) {
      const LocationBaseStr = `${lon},${lat}`;
      const apiUrl1 = `https://api.mapbox.com/directions/v5/mapbox/driving/${Location1Str};${LocationBaseStr}?alternatives=true&geometries=geojson&language=en&overview=full&steps=true&access_token=${process.env.MAPBOX_ACCESS_TOKEN}`;
      const fetchResult1 = await fetch(apiUrl1);
      const Result1 = await fetchResult1.json();

      if (Result1.routes && Result1.routes[0]) {
        deadhead_distance = parseFloat(Number(Result1.routes[0].distance / 1609.34).toFixed(2));
      }
    }

    if (Result.routes && Result.routes[0]) {
      const { distance, duration } = Result.routes[0];
      setMapData({
        ...mapData,
        distance: parseFloat(Number(distance / 1609.34).toFixed(2)),
        duration: parseFloat(Number(duration / 60).toFixed(2)),
        ...(deadhead_distance > 0 && { deadhead_distance }),
      });

      setDirections([...Result.routes[0].geometry.coordinates]);
    }
  }

  /*
  useEffect(() => {
    let lat = 0;
    let lon = 0;
    if (!siteSetting() || siteSetting().lat == '0' || siteSetting().lon == '0') {
      lat = 40.907147;
      lon = -73.872336;
    } else {
      lat = siteSetting().lat / 1000000.0;
      lon = siteSetting().lon / 1000000.0;
    }
    
    setMapCenter([parseFloat(lon), parseFloat(lat)]);
  }, []);
  */
  useEffect(() => {
    if (
      mapType === 'GOOGLE' ||
      (mapData.pickup_lat === 0 && mapData.pickup_lng === 0) ||
      (mapData.dropoff_lat === 0 && mapData.dropoff_lng === 0)
    ) {
      setDirections([]);
      return;
    }

    getDirections();
  }, [mapData.pickup_lat, mapData.pickup_lng, mapData.dropoff_lat, mapData.dropoff_lng]);

  useEffect(() => {
    if (mapType === 'GOOGLE') {
      return;
    }

    if (mapData.pickup_lat + mapData.pickup_lng + mapData.dropoff_lat + mapData.dropoff_lng == 0) {
      return;
    }

    if (mapData.pickup_lat + mapData.pickup_lng !== 0 && mapData.dropoff_lat + mapData.dropoff_lng === 0) {
      setMapCenter([parseFloat(mapData.pickup_lng), parseFloat(mapData.pickup_lat)]);
      setFitBounds(null);
    } else if (mapData.pickup_lat + mapData.pickup_lng === 0 && mapData.dropoff_lat + mapData.dropoff_lng !== 0) {
      setMapCenter([parseFloat(mapData.dropoff_lng), parseFloat(mapData.dropoff_lat)]);
      setFitBounds(null);
    } else if (mapData.driver_lat === 0 && mapData.driver_lng === 0) {
      setFitBounds([
        [parseFloat(mapData.pickup_lng), parseFloat(mapData.pickup_lat)],
        [parseFloat(mapData.dropoff_lng), parseFloat(mapData.dropoff_lat)],
      ]);
    } else {
      setFitBounds([
        [
          Math.min(parseFloat(mapData.pickup_lng), parseFloat(mapData.dropoff_lng), parseFloat(mapData.driver_lng)),
          Math.min(parseFloat(mapData.pickup_lat), parseFloat(mapData.dropoff_lat), parseFloat(mapData.driver_lat)),
        ],
        [
          Math.max(parseFloat(mapData.pickup_lng), parseFloat(mapData.dropoff_lng), parseFloat(mapData.driver_lng)),
          Math.max(parseFloat(mapData.pickup_lat), parseFloat(mapData.dropoff_lat), parseFloat(mapData.driver_lat)),
        ],
      ]);
    }
  }, [
    mapData.pickup_lat,
    mapData.pickup_lng,
    mapData.dropoff_lat,
    mapData.dropoff_lng,
    mapData.driver_lat,
    mapData.driver_lng,
  ]);

  useEffect(() => {
    const applyRouteLayers = layers => {
      setMapRouteDirections(layers.directions);
      setMapRouteConnectors(layers.connectors);
      if (layers.fitBounds) {
        setFitBounds(layers.fitBounds);
      }
    };

    const calcRouteDirections = async () => {
      if (!isRouteViewMode) {
        showCircularProgress('Loading');
      }
      try {
        const layers = await computeRouteViewMapLayers(
          mapData.routeViewData,
          siteSetting().timezone
        );
        applyRouteLayers(layers);
      } finally {
        if (!isRouteViewMode) {
          hideCircularProgress();
        }
      }
    };

    if (mapData?.routeViewLayers) {
      applyRouteLayers(mapData.routeViewLayers);
      return;
    }

    if (mapData?.routeViewData?.length > 0) {
      setMapRouteDirections(null);
      setMapRouteConnectors(null);
      calcRouteDirections();
      return;
    }

    setMapRouteDirections(null);
    setMapRouteConnectors(null);
  }, [mapData.routeViewData, mapData.routeViewLayers, isRouteViewMode]);

  useEffect(() => {
    setHoveredRouteJobId(null);
  }, [mapData.routeViewData]);

  const handleMapStyleLoad = useCallback(map => {
    mapRef.current = map;
    map.on('mouseleave', () => {
      setHoveredRouteJobId(null);
      if (map.getCanvas()) {
        map.getCanvas().style.cursor = '';
      }
    });
  }, []);

  const handleRouteStopEnter = useCallback(jobId => {
    markerHoverRef.current = true;
    setHoveredRouteJobId(String(jobId));
    if (mapRef.current?.getCanvas()) {
      mapRef.current.getCanvas().style.cursor = 'pointer';
    }
  }, []);

  const handleRouteStopLeave = useCallback(() => {
    markerHoverRef.current = false;
  }, []);

  const handleRouteMapMouseMove = useCallback(
    (map, event) => {
      if (!isRouteViewMode || !mapRouteDirections?.length) {
        return;
      }

      if (markerHoverRef.current) {
        return;
      }

      const tripHitLayers = mapRouteDirections
        .filter(detail => detail.direction?.length > 0)
        .map(detail => `route-hit-${detail.id}`);
      const connectorHitLayers =
        mapRouteConnectors?.filter(connector => connector.direction?.length > 0).map(connector => `connector-hit-${connector.id}`) ||
        [];
      const hitLayers = [...tripHitLayers, ...connectorHitLayers];

      if (!hitLayers.length) {
        setHoveredRouteJobId(null);
        return;
      }

      const { x, y } = event.point;
      const features = map.queryRenderedFeatures(
        [
          [x - 12, y - 12],
          [x + 12, y + 12],
        ],
        { layers: hitLayers }
      );

      const hitLayerId = features[0]?.layer?.id;
      if (hitLayerId?.startsWith('route-hit-')) {
        setHoveredRouteJobId(hitLayerId.replace('route-hit-', ''));
        map.getCanvas().style.cursor = 'pointer';
      } else if (hitLayerId?.startsWith('connector-hit-')) {
        const connector = mapRouteConnectors.find(item => `connector-hit-${item.id}` === hitLayerId);
        setHoveredRouteJobId(connector?.toJobId != null ? String(connector.toJobId) : null);
        map.getCanvas().style.cursor = 'pointer';
      } else {
        setHoveredRouteJobId(null);
        map.getCanvas().style.cursor = '';
      }
    },
    [isRouteViewMode, mapRouteDirections, mapRouteConnectors]
  );

  const routeStopMarkers = useMemo(
    () => (mapRouteDirections?.length > 0 ? buildRouteStopMarkers(mapRouteDirections) : []),
    [mapRouteDirections]
  );

  const routeHoverFocus = useMemo(
    () => getRouteHoverFocus(hoveredRouteJobId, mapRouteDirections),
    [hoveredRouteJobId, mapRouteDirections]
  );

  const mapContent = useMemo(() => {
    return (
      <div className="main--map-body" style={{ backgroundImage: `url(${TmpMapBack})` }}>
        {isRouteViewMode && mapData.routeViewSummary && (
          <div className="route-map-toolbar">
            <div>
              <div className="route-map-driver">{mapData.routeViewSummary.driverLabel}</div>
              <div className="route-map-meta">
                {[mapData.routeViewSummary.vehicleLabel, mapData.routeViewSummary.window].filter(Boolean).join(' | ')}
              </div>
            </div>
            <div className="route-map-stats">
              <div className="route-map-stat-box">
                <strong>{mapData.routeViewSummary.trips}</strong>
                <span>Trips</span>
              </div>
              <div className="route-map-stat-box">
                <strong>{mapData.routeViewSummary.serviceMiles?.toFixed(1)}</strong>
                <span>Trip Miles</span>
              </div>
              <div className="route-map-stat-box">
                <strong>{mapData.routeViewSummary.connectorMiles?.toFixed(1)}</strong>
                <span>Between Trips</span>
              </div>
            </div>
          </div>
        )}
        {mapType === 'GOOGLE' && (
          <AsyncMap
            googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${
              siteSetting().settings.google_maps_api_key
            }&v=3.exp&&libraries=geometry,drawing,places`}
            loadingElement={<div style={{ height: '100%' }} />}
            containerElement={<div style={{ height: '100%' }} />}
            mapElement={<div style={{ height: '100%' }} />}
          />
        )}
        <div className="route-map-stage">
        {mapType === 'MAPBOX' &&
          (mapData.pickup_lat !== 0 ||
            mapData.pickup_lng !== 0 ||
            mapData.dropoff_lat !== 0 ||
            mapData.dropoff_lng !== 0 ||
            mapRouteDirections?.length > 0) &&
          (mapCenter || fitBounds) && (
            <Map
              style="mapbox://styles/mapbox/streets-v9"
              fitBoundsOptions={{
                padding: { top: 80, bottom: 80, left: 80, right: 80 },
              }}
              containerStyle={{
                height: '100%',
                width: '100%',
              }}
              {...(fitBounds && { fitBounds })}
              {...(mapCenter && { center: mapCenter })}
              onStyleLoad={handleMapStyleLoad}
              onMouseMove={handleRouteMapMouseMove}
            >
              {!(mapData.pickup_lat === 0 && mapData.pickup_lng === 0) && (
                <Marker coordinates={[mapData.pickup_lng, mapData.pickup_lat]} anchor="center">
                  <PickupMarkerDot />
                </Marker>
              )}
              {!(mapData.dropoff_lat === 0 && mapData.dropoff_lng === 0) && (
                <Marker coordinates={[mapData.dropoff_lng, mapData.dropoff_lat]} anchor="center">
                  <DropoffMarkerDot />
                </Marker>
              )}
              {!(mapData.driver_lat === 0 && mapData.driver_lng === 0) && (
                <Marker coordinates={[mapData.driver_lng, mapData.driver_lat]} anchor="bottom">
                  <img src={GreenIcon} alt="map-vehicle" />
                </Marker>
              )}
              {directions?.length > 0 && (
                <Layer
                  type="line"
                  layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                  paint={{ 'line-color': '#4790E5', 'line-width': 8 }}
                >
                  <Feature coordinates={directions} />
                </Layer>
              )}
              {mapRouteConnectors?.length > 0 &&
                mapRouteConnectors.map(connector =>
                  connector.direction?.length > 0 ? (
                    <React.Fragment key={connector.id}>
                      <Layer
                        id={`connector-hit-${connector.id}`}
                        type="line"
                        layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                        paint={{ 'line-color': '#000000', 'line-width': 14, 'line-opacity': 0 }}
                      >
                        <Feature coordinates={connector.direction} />
                      </Layer>
                      <Layer
                        type="line"
                        layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                        paint={getConnectorPaint(connector, routeHoverFocus)}
                      >
                        <Feature coordinates={connector.direction} />
                      </Layer>
                    </React.Fragment>
                  ) : null
                )}
              {routeStopMarkers.map(stop => {
                const isStopFocused = isRouteStopFocused(stop, routeHoverFocus);
                const isStopDimmed = routeHoverFocus.jobId != null && !isStopFocused;

                return (
                  <Marker key={stop.id} coordinates={[stop.lon, stop.lat]} anchor="center">
                    <div
                      className={`map-route-marker-wrap${isStopFocused ? ' is-focused' : ''}${isStopDimmed ? ' is-dimmed' : ''}`}
                      style={{
                        transform: `translate(${stop.offsetX}px, ${stop.offsetY}px)`,
                        zIndex: isStopFocused ? 30 : 10 + stop.stackOrder,
                        opacity: isStopDimmed ? 0.22 : 1,
                      }}
                    >
                      <div
                        className="map-route-marker-hit"
                        onMouseEnter={() => handleRouteStopEnter(stop.jobId)}
                        onMouseLeave={handleRouteStopLeave}
                        aria-hidden
                      />
                      <p
                        className={`map-route-marker-label map-route-marker-label--${stop.stopType} map-route-marker-label--${stop.labelSlot}`}
                      >
                        {stop.jobId}
                        <br />
                        {stop.time}
                      </p>
                      {stop.stopType === 'pickup' ? <PickupMarkerDot /> : <DropoffMarkerDot />}
                    </div>
                  </Marker>
                );
              })}
              {mapRouteDirections?.length > 0 &&
                mapRouteDirections.map(detail =>
                  detail.direction?.length > 0 ? (
                    <React.Fragment key={`route-trip-${detail.id}`}>
                      <Layer
                        id={`route-hit-${detail.id}`}
                        type="line"
                        layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                        paint={{ 'line-color': '#000000', 'line-width': 14, 'line-opacity': 0 }}
                      >
                        <Feature coordinates={detail.direction} />
                      </Layer>
                      <Layer
                        id={`route-line-${detail.id}`}
                        type="line"
                        layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                        paint={getTripRoutePaint(detail, routeHoverFocus)}
                      >
                        <Feature coordinates={detail.direction} />
                      </Layer>
                    </React.Fragment>
                  ) : null
                )}
            </Map>
          )}
        </div>
        <div className={`map_legend${isRouteViewMode ? ' route-view-legend' : ''}`}>
          <div className="map_icon_container">
            <span className="legend-dot legend-dot--pickup" />
            <p>Pickup</p>
          </div>
          <div className="map_icon_container">
            <span className="legend-dot legend-dot--dropoff" />
            <p>Dropoff</p>
          </div>
          {isRouteViewMode ? (
            <>
              <div className="map_icon_container">
                <span className="legend-line service" />
                <p>Trip route</p>
              </div>
              <div className="map_icon_container">
                <span className="legend-line complete" />
                <p>Completed trip</p>
              </div>
              <div className="map_icon_container">
                <span className="legend-line connector" />
                <p>Between trips</p>
              </div>
            </>
          ) : (
            <div className="map_icon_container">
              <img alt="" className="map_icon" src="https://maps.google.com/mapfiles/ms/icons/green.png" />
              <p>Vehicle</p>
            </div>
          )}
        </div>
      </div>
    );
  }, [
    mapData.pickup_lat,
    mapData.pickup_lng,
    mapData.dropoff_lat,
    mapData.dropoff_lng,
    mapData.driver_lat,
    mapData.driver_lng,
    mapData.routeViewData,
    directions,
    fitBounds,
    mapCenter,
    mapType,
    mapRouteDirections,
    mapRouteConnectors,
    routeStopMarkers,
    hoveredRouteJobId,
    routeHoverFocus,
    isRouteViewMode,
    mapData.routeViewSummary,
    handleMapStyleLoad,
    handleRouteMapMouseMove,
    handleRouteStopEnter,
    handleRouteStopLeave,
  ]);

  return (
    <MapSectionWrapper>
      <Paper className="main--map-section" elevation={3} square>
        <PergoTitle className="main--map-title">{isRouteViewMode ? 'Route Map' : 'Job Map'}</PergoTitle>
        {mapContent}
      </Paper>
    </MapSectionWrapper>
  );
};

export default React.memo(MapSection);

const MapSectionWrapper = styled.div`
  flex: auto;
  display: flex;

  .main--map-section {
    flex: auto;
    margin-right: 10px;
    border: 2px solid var(--green-main-color);
    height: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;

    .main--map-title {
      background: var(--green-main-color);
      font-size: 16px;
      padding: 15px 10px;
    }

    .main--map-body {
      height: 100%;
      position: relative;
      background-position: center;
      background-size: cover;
      display: flex;
      flex-direction: column;

      .route-map-toolbar {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        flex-wrap: wrap;
        padding: 10px;
        background: #f5f7f8;
        border-bottom: 1px solid #cfd8dd;
        z-index: 2;
      }

      .route-map-driver {
        color: #0c4765;
        font-size: 22px;
        font-weight: 700;
      }

      .route-map-meta {
        margin-top: 4px;
        color: #67757d;
        font-size: 13px;
      }

      .route-map-stats {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        justify-content: flex-end;
      }

      .route-map-stage {
        flex: 1;
        position: relative;
        min-height: 0;
      }

      .map-marker-dot {
        display: block;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: 3px solid #ffffff;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
        box-sizing: border-box;
        flex-shrink: 0;
      }

      .map-marker-dot--pickup {
        background: #ff6e63;
      }

      .map-marker-dot--dropoff {
        background: #5b88f6;
      }

      .map-route-marker-wrap {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        pointer-events: none;
        transition: opacity 0.15s ease;
      }

      .map-route-marker-hit {
        position: absolute;
        left: 50%;
        top: 50%;
        width: 104px;
        height: 56px;
        transform: translate(-50%, -50%);
        pointer-events: auto;
        cursor: pointer;
      }

      .map-route-marker-label {
        position: absolute;
        margin: 0;
        padding: 2px 4px;
        background: #ffffff;
        text-align: center;
        font-size: 11px;
        line-height: 1.2;
        font-weight: 700;
        color: #1a2d39;
        white-space: nowrap;
        border-radius: 2px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
        pointer-events: none;
      }

      .map-route-marker-label--pickup {
        border: 1px solid #ff6e63;
      }

      .map-route-marker-label--dropoff {
        border: 1px solid #5b88f6;
      }

      .map-route-marker-label--slot-0.map-route-marker-label--pickup {
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        margin-bottom: 4px;
      }

      .map-route-marker-label--slot-0.map-route-marker-label--dropoff {
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        margin-top: 4px;
      }

      .map-route-marker-label--slot-1 {
        bottom: 100%;
        left: 100%;
        margin-bottom: 2px;
        margin-left: 4px;
      }

      .map-route-marker-label--slot-2 {
        bottom: 100%;
        right: 100%;
        margin-bottom: 2px;
        margin-right: 4px;
      }

      .map-route-marker-label--slot-3 {
        top: 50%;
        left: 100%;
        transform: translateY(-50%);
        margin-left: 6px;
      }

      .map-route-marker-label--slot-4 {
        top: 50%;
        right: 100%;
        transform: translateY(-50%);
        margin-right: 6px;
      }

      .map-route-marker-label--slot-5 {
        top: 100%;
        left: 100%;
        margin-top: 2px;
        margin-left: 4px;
      }

      .map-route-marker-label--slot-6 {
        top: 100%;
        right: 100%;
        margin-top: 2px;
        margin-right: 4px;
      }

      .map-route-marker-label--slot-7 {
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        margin-top: 4px;
      }

      .route-map-stat-box {
        min-width: 92px;
        padding: 8px 10px;
        border: 1px solid #d0d8dd;
        background: #ffffff;
        text-align: center;

        strong {
          display: block;
          color: #0c4765;
          font-size: 18px;
        }

        span {
          color: #67757d;
          font-size: 11px;
        }
      }

      .map_legend {
        width: 100px;
        height: 120px;
        background-color: white;
        position: absolute;
        bottom: 20px;
        right: 20px;
        border: 1px solid black;
        display: flex;
        flex-direction: column;

        .map_icon_container {
          display: flex;
          align-items: center;
          flex: 1;

          .map_icon {
            width: 25px;
            height: 25px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 8px;
          }

          p {
            font-size: 14px;
            font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
            margin: 0;
            padding: 0;
          }

          .legend-dot {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            border: 2px solid #ffffff;
            box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12);
            display: inline-block;
            margin: 0 8px;
            flex-shrink: 0;
            box-sizing: border-box;
          }

          .legend-dot--pickup {
            background: #ff6e63;
          }

          .legend-dot--dropoff {
            background: #5b88f6;
          }

          .legend-line {
            width: 20px;
            height: 0;
            border-top: 3px solid;
            display: inline-block;
            margin: 0 8px;

            &.service {
              border-color: #178cc7;
            }

            &.complete {
              border-color: #25d84f;
            }

            &.connector {
              border-color: #ff9d2e;
              border-top-style: dashed;
            }
          }
        }

        &.route-view-legend {
          width: 140px;
          height: auto;
          min-height: 180px;
          padding: 4px 0;
        }
      }
    }
  }

  @media (max-width: 1299px) {
    flex-direction: column;

    .main--map-section {
      margin-right: 0;
    }

    .main--driver-section {
      max-width: 100%;
      max-height: 160px;
    }
  }
  @media (max-width: 830px) {
    .main--map-section {
      height: 400px;
    }

    .main--driver-section {
      .main--driver-body {
      }
    }
  }
`;
